import React, {
  MouseEvent,
  TouchEvent,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react"
import styled, { css } from "styled-components"
import Arrow from "../../images/arrow.inline.svg"
import { Slide } from "./slider-item.component"
import OriginalSliderItem from "./slider-item.component"
import {
  AppFunctionComponent,
  ListItemBuilder,
  RenderRichText,
} from "../../types"
import { ScrollAnchor } from "./sections/custom/innovation-lab-form/innovation-lab.component"
import { renderRichText } from "gatsby-source-contentful/rich-text"
import { SliderButtonStyle } from "./sections/custom/quick-start/quick-start.styled"
import useTranslationHook from "../../hooks/use-translation.hook"
import {
  ControlsNextIcon,
  ControlsPrevIcon,
} from "./slider/slider-arrows-text.component"

const sliderLayoutBreakpoint = 440

const SliderWrapper = styled.div`
  padding: 0;
  overflow: hidden;
  position: relative;
  background: #f4f5f7;
  padding: 112px 0;
  @media (max-width: 768px) {
    padding: 64px 0;
  }
`

const Slider = styled.div`
  margin: 0 auto;
  max-width: 1260px;
  width: calc(100% - 30px);
  @media (min-width: 768px) {
    width: calc(100% - 60px);
  }
`

const SliderText = styled.div`
  margin: 0 auto;
  max-width: 1260px;
  width: calc(100% - 30px);
  margin-bottom: 80px;

  @media (min-width: 768px) {
    width: calc(100% - 60px);
  }
  h2 {
    font-family: Facto;
    font-size: 23px;
    font-style: normal;
    font-weight: 500;
    line-height: 120%; /* 27.6px */
  }
`

const slidesList = []

const SliderTrack = styled.div<{
  currentSlideIndex: number
  itemsPerColumn: number
}>`
  width: 100%;
  display: flex;
  user-select: none;
  flex-direction: row;
  transition: transform 0.5s ease-in-out;

  ${({ currentSlideIndex }) => css`
    --position-Y: ${currentSlideIndex}px;
    --position-X: -${currentSlideIndex * 260}px;

    ${currentSlideIndex === slidesList.length - 1 &&
    css`
      --position-X: calc(-${currentSlideIndex * 260}px + (100% - 245px));
    `};

    @media (max-width: 767px) {
      flex-wrap: nowrap; /* Crucial: ensure columns are in a row for sliding */
      --position-X: calc(
        -${currentSlideIndex * 100}% - ${currentSlideIndex * 15}px
      ); /* Slide by column width */
    }

    @media (min-width: ${sliderLayoutBreakpoint}px) and (min-width: 768px) {
      --position-X: calc(
        -${currentSlideIndex * 50}% - ${currentSlideIndex * 7.5}px
      );

      ${currentSlideIndex === slidesList.length - 1 &&
      css`
        --position-X: calc(
          -${(currentSlideIndex - 1) * 50}% - ${(currentSlideIndex - 1) * 7.5}px
        );
      `};
    }

    @media (min-width: 768px) {
      flex-wrap: nowrap; /* Revert to row layout for desktop */
      --position-X: calc(
        -${currentSlideIndex * 50}% - ${currentSlideIndex * 30}px
      );

      ${currentSlideIndex === slidesList.length - 1 &&
      css`
        --position-X: calc(
          -${(currentSlideIndex - 1) * 50}% - ${(currentSlideIndex - 1) * 30}px
        );
      `};
    }

    @media (min-width: 1240px) {
      --position-X: calc(-${currentSlideIndex * 412}px);

      ${currentSlideIndex === slidesList.length - 1 &&
      css`
        --position-X: calc(-${(currentSlideIndex - 1) * 412}px);
      `};
    }
  `};
  transform: translate3d(var(--position-X), 0, var(--position-Y));
`

// This will now be the container for a *column* of items on mobile
const SlideItem = styled.div<{ style?: ListItemBuilder["style"] }>`
  cursor: grab;
  user-select: none;

  @media (max-width: 767px) {
    min-width: 100%; /* Each column slide takes full width */
    max-width: 100%;
    margin-right: 15px; /* Give some spacing between columns if needed, or remove */
    box-sizing: border-box;
    display: flex;
    gap: 48px;
    flex-direction: column; /* Stack items vertically within the column */
    align-items: center; /* Optional: center items in the column */
  }

  // Desktop styles (largely based on previous SlideItem)
  @media (min-width: 768px) {
    min-width: 245px;
    max-width: 560px;
    &:not(:last-of-type) {
      margin-right: 15px;
    }
  }

  @media (min-width: ${sliderLayoutBreakpoint}px) and (min-width: 768px) {
    min-width: calc(50% - 7.5px);
  }

  @media (min-width: 768px) {
    min-width: calc(50% - 15px);
    &:not(:last-of-type) {
      margin-right: 30px;
    }
  }

  @media (min-width: 1240px) {
    min-width: 300px;
    max-width: 300px;
    &:not(:last-of-type) {
      margin-right: 56px;
      padding-right: 56px;
      border-right: 1px solid #d4d9e4;
    }
  }

  // Common styles for h2, p (can be moved or duplicated if ColumnDisplayItem needs different ones)
  h2,
  p {
    padding-left: 0;
    padding-right: 0;
  }

  h2 {
    font-family: Facto;
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: 130%;

    @media (max-width: 768px) {
      font-size: 16px;
    }
  }

  p {
    color: #5b5b5b;
    font-family: Montserrat;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
  }

  ${({ style }) =>
    style?.text_align === "center" &&
    css`
      text-align: center;
    `}

  ${({ style }) =>
    style?.image_height &&
    css`
      img {
        height: ${style.image_height} !important;
        min-height: ${style.image_height} !important;
        margin-bottom: 48px;
      }
    `}
`

// New component to style individual items within a column on mobile
// This is now correctly based on the imported functional SliderItem
const ColumnDisplayItem = styled(OriginalSliderItem)<{
  style?: ListItemBuilder["style"]
}>`
  ${({ style }) =>
    style?.image_height &&
    css`
      img {
        height: ${style.image_height} !important;
        min-height: ${style.image_height} !important;
        margin-bottom: 48px;
      }
    `}
  @media (max-width: 767px) {
    width: 100%; // Take full width of the column
    max-width: none; // Override any max-width from OriginalSliderItem styles if necessary
    min-width: auto; // Override any min-width
    margin-right: 0;
    margin-bottom: 20px; /* Space between items in a column */
    box-sizing: border-box;
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto;
    grid-column-gap: 24px;
    grid-template-areas:
      "Image Heading"
      "Image Text";

    // If OriginalSliderItem has padding/border that needs to be reset for column display:
    // padding: 0;
    // border: none;

    &:last-child {
      margin-bottom: 0;
    }

    // Targeting h2 and p specifically within ColumnDisplayItem for mobile column view
    h2,
    p {
      padding-left: 0; // Reset padding if needed for column items
      padding-right: 0;
      text-align: left; // Or whatever alignment is desired for items in a column
    }

    h2 {
      grid-area: Heading;
    }
    p {
      grid-area: Text;
    }
    img {
      margin-bottom: 0 !important;
      height: 48px !important;
      min-height: 48px !important;
    }
  }
  // Desktop styles for ColumnDisplayItem should not interfere if it's only for mobile columns
  // However, if OriginalSliderItem has desktop styles, they will apply unless overridden here.
`

export const ArrowIcon = styled(Arrow)`
  height: 15px;
  user-selectable: none;
  @media (min-width: 960px) {
    height: 22px;
  }
`

export const ArrowContainer = styled.div<{ active: boolean }>`
  opacity: 1;
  width: 32px;
  height: 32px;
  display: flex;
  cursor: pointer;
  border-radius: 50%;
  align-items: center;
  flex-direction: row;
  pointer-events: all;
  visibility: visible;
  user-selectable: none;
  justify-content: center;
  background-color: ${({ theme }) => theme.color.arrowBackgroundDark};
  transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;

  @media (min-width: 960px) {
    width: 50px;
    height: 50px;
  }

  ${({ active }) =>
    !active &&
    css`
      opacity: 0;
      visibility: hidden;
    `};
`

export const ArrowPrev = styled(ArrowContainer)`
  svg {
    transform: rotateZ(180deg);
  }
`

export const ArrowNext = styled(ArrowContainer)``

export const ArrowsWrapper = styled.div`
  display: flex;
  max-width: 1260px;
  margin: 0 auto;
  /* position: absolute; */
  flex-direction: row;
  pointer-events: none;
  width: calc(100vw - 60px);
  justify-content: space-between;

  @media (min-width: ${sliderLayoutBreakpoint}px) {
  }

  @media (min-width: 768px) {
  }

  @media (min-width: 960px) {
  }

  @media (min-width: 1240px) {
  }

  @media (min-width: 1430px) {
  }
`

export const StyledControlsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  margin-top: 20px;
  gap: 24px;

  padding-left: 20px;
  padding-right: 20px;

  @media (min-width: 768px) {
    padding-left: 50px;
    padding-right: 50px;
    justify-content: flex-end;
    margin-top: 56px;
  }
  @media (min-width: 1260px) {
    padding-left: calc((100% - 1260px) / 2);
    padding-right: calc((100% - 1260px) / 2);
  }
`

interface Props {
  slides?: Record<string, any>[]
  className?: string
  anchorId?: string
  text?: RenderRichText
}

const SliderWithBackgroundContainer: AppFunctionComponent<Props> = ({
  className,
  slides = [],
  text,
  anchorId,
}) => {
  const windowAvailable = typeof window !== `undefined`
  const { translate } = useTranslationHook()
  const ITEMS_PER_COLUMN_MOBILE = 2

  const [isMobile, setIsMobile] = useState(
    windowAvailable ? window.innerWidth < 768 : false
  )

  // Recalculate on resize
  useEffect(() => {
    if (!windowAvailable) return
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [windowAvailable])

  const calculateLastPossibleSlideIndex = useCallback(() => {
    if (isMobile) {
      if (!slides || slides.length === 0) return 0
      return Math.ceil(slides.length / ITEMS_PER_COLUMN_MOBILE) - 1
    }
    // Desktop logic
    let lastIndex = slides.length - 1
    if (windowAvailable) {
      if (window.innerWidth >= 1240) {
        lastIndex = slides.length - 3 >= 0 ? slides.length - 3 : 0
      } else if (window.innerWidth >= sliderLayoutBreakpoint) {
        lastIndex = slides.length - 2 >= 0 ? slides.length - 2 : 0
      }
    }
    return lastIndex < 0 ? 0 : lastIndex
  }, [
    isMobile,
    slides,
    windowAvailable,
    ITEMS_PER_COLUMN_MOBILE,
    sliderLayoutBreakpoint,
  ])

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const [lastPossibleSlideIndex, setLastPossibleSlideIndex] = useState(
    calculateLastPossibleSlideIndex()
  )

  // Effect to reset slide index and update last possible index when view mode or slides change
  useEffect(() => {
    setCurrentSlideIndex(0)
    setLastPossibleSlideIndex(calculateLastPossibleSlideIndex())
  }, [isMobile, slides, calculateLastPossibleSlideIndex])

  const slide = (i: number) => {
    setCurrentSlideIndex(i)
  }

  const prev = () => {
    let newIndex =
      currentSlideIndex > 0 ? currentSlideIndex - 1 : currentSlideIndex
    if (!isMobile) {
      // Use isMobile state
      if (
        slides.length - 1 === currentSlideIndex &&
        window.innerWidth >= sliderLayoutBreakpoint
      ) {
        newIndex = newIndex - 1
      }
    }
    slide(newIndex)
  }

  const next = () => {
    const lastSlideIndex = calculateLastPossibleSlideIndex() // Recalculate to be sure
    const newIndex =
      currentSlideIndex < lastSlideIndex
        ? currentSlideIndex + 1
        : currentSlideIndex
    setLastPossibleSlideIndex(lastSlideIndex)
    slide(newIndex)
  }

  let touchStartX = 0
  let touchStartY = 0

  const touchStart = (e: TouchEvent<HTMLDivElement>) => {
    touchStartX = e.touches[0].clientX
    touchStartY = e.touches[0].clientY
  }

  const touchMove = (e: TouchEvent<HTMLDivElement>) => {
    const touchEndX = e.touches[0].clientX
    const touchEndY = e.touches[0].clientY

    const diffX = Math.abs(touchStartX - touchEndX)
    const diffY = Math.abs(touchStartY - touchEndY)

    if (diffX > 40 && diffY < 30) {
      touchEndX - touchStartX > 0 ? prev() : next()
    }
  }

  let mouseStartX = 0
  let mouseEndX = 0

  const mouseDown = (e: MouseEvent<HTMLDivElement>) => {
    mouseStartX = e.clientX
  }

  const mouseUp = (e: MouseEvent<HTMLDivElement>) => {
    mouseEndX = e.clientX
    const diffX = Math.abs(mouseStartX - mouseEndX)
    if (diffX > (isMobile ? 50 : 100)) {
      // Use isMobile state
      mouseEndX - mouseStartX > 0 ? prev() : next()
    }
  }

  const displayableSlides = useMemo(() => {
    if (isMobile) {
      // Use isMobile state
      const result = []
      if (!slides || slides.length === 0) return []
      for (let i = 0; i < slides.length; i += ITEMS_PER_COLUMN_MOBILE) {
        result.push(slides.slice(i, i + ITEMS_PER_COLUMN_MOBILE))
      }
      return result
    }
    return [] // Not used in desktop path, desktop maps `slides` directly
  }, [slides, isMobile, ITEMS_PER_COLUMN_MOBILE])

  return (
    <SliderWrapper
      className={className}
      onMouseDown={mouseDown}
      onMouseUp={mouseUp}
      onTouchStart={touchStart}
      onTouchMove={touchMove}
    >
      {text && <SliderText>{renderRichText(text)}</SliderText>}
      <ScrollAnchor id={anchorId} />
      <Slider>
        <SliderTrack
          currentSlideIndex={currentSlideIndex}
          itemsPerColumn={isMobile ? 1 : ITEMS_PER_COLUMN_MOBILE}
        >
          {isMobile
            ? displayableSlides.map(
                (slideColumn: Slide[], columnIndex: number) => (
                  <SlideItem key={`column-${columnIndex}`}>
                    {slideColumn.map(
                      (slideItemData: Slide, itemIndex: number) => (
                        <ColumnDisplayItem
                          slide={slideItemData}
                          key={`mobile-item-${columnIndex}-${itemIndex}`}
                          style={slideItemData.style}
                          className="column-inner-item"
                        />
                      )
                    )}
                  </SlideItem>
                )
              )
            : slides.map((slideItemData: Slide, index: number) => (
                <SlideItem
                  key={`desktop-wrapper-${index}`}
                  className="desktop-slide-wrapper"
                >
                  <ColumnDisplayItem
                    slide={slideItemData}
                    style={slideItemData.style}
                    className="desktop-inner-item"
                  />
                </SlideItem>
              ))}
        </SliderTrack>
      </Slider>
      <StyledControlsContainer>
        <SliderButtonStyle
          onClick={prev}
          disabledPrev={currentSlideIndex === 0}
        >
          <ControlsPrevIcon /> {translate("PREV")}
        </SliderButtonStyle>
        <SliderButtonStyle
          onClick={next}
          disabledNext={currentSlideIndex === lastPossibleSlideIndex}
        >
          {translate("NEXT")} <ControlsNextIcon />
        </SliderButtonStyle>
      </StyledControlsContainer>
    </SliderWrapper>
  )
}

export default SliderWithBackgroundContainer
